"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import RandomProblems from "@/components/RandomProblems";
import {
  selectQuizScopes,
  type QuizClass,
  type SubjectProblem,
  type SubjectScope,
} from "@/lib/quiz-problems";

interface QuizProblemsProps {
  class: QuizClass | ["theory", "coding"];
}

const QuizProblems: React.FC<QuizProblemsProps> = ({ class: classProp }) => {
  const [subjectsProblems, setSubjectsProblems] = useState<SubjectProblem[]>([]);
  const [subjectScopes, setSubjectScopes] = useState<SubjectScope[]>([]);
  const [scopes, setScopes] = useState<{ scope: string; count: number }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const preparedClassKeyRef = useRef<string | null>(null);
  const classKey = Array.isArray(classProp) ? classProp.join("|") : classProp;

  const resampleScopes = React.useCallback(() => {
    const classes = classKey.split("|") as QuizClass[];
    setScopes(selectQuizScopes(subjectsProblems, subjectScopes, classes));
  }, [classKey, subjectScopes, subjectsProblems]);

  // JSON 파일 로드
  useEffect(() => {
    const loadData = async () => {
      try {
        const [subjectsResponse, scopesResponse] = await Promise.all([
          fetch("/subjects-problems.json"),
          fetch("/subject-scope-count.json"),
        ]);

        const subjectsData: SubjectProblem[] = await subjectsResponse.json();
        const scopesData: SubjectScope[] = await scopesResponse.json();

        setSubjectsProblems(subjectsData);
        setSubjectScopes(scopesData);
      } catch (error) {
        console.error("데이터 로드 실패:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // 문제수 할당 및 scope 선택
  useEffect(() => {
    if (subjectsProblems.length === 0 || subjectScopes.length === 0) {
      return;
    }

    if (preparedClassKeyRef.current === classKey) {
      return;
    }

    // Math.random 기반 비순수 계산 결과는 hydration 이후 effect에서만 state로 저장한다.
    resampleScopes();
    preparedClassKeyRef.current = classKey;
  }, [classKey, resampleScopes]);

  // unsolvedOnly 모드에서 사용할 해당 class의 전체 scopes (JCG-1638)
  // 특정 scope들만 풀에 넣으면 이미 푼 비율이 높아 10개 미만이 출제될 수 있으므로
  // unsolvedOnly=true일 때는 해당 class의 모든 scope를 풀에 포함
  const allClassScopes = useMemo(() => {
    if (subjectsProblems.length === 0 || subjectScopes.length === 0) return [];
    const classes = Array.isArray(classProp) ? classProp : [classProp];
    const result: { scope: string; count: number }[] = [];
    for (const ss of subjectScopes) {
      const isRelevant = subjectsProblems.some(
        (sp) => classes.includes(sp.class) && sp.subject.toLowerCase() === ss.subject.toLowerCase()
      );
      if (!isRelevant) continue;
      for (const sc of ss.scopes) {
        result.push({ scope: sc.scope, count: 1 });
      }
    }
    return result;
  }, [classProp, subjectsProblems, subjectScopes]);

  if (isLoading) {
    return (
      <div className="my-10 text-center">
        <p className="text-gray-600 dark:text-gray-400">문제를 준비하고 있습니다...</p>
      </div>
    );
  }

  if (scopes.length === 0) {
    return (
      <div className="my-10 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          문제를 생성할 수 없습니다.
        </p>
      </div>
    );
  }

  // 원래 가중치 분포 scopes를 primary로 사용 (출제 비율 유지)
  // unsolvedOnly=true일 때 primary scope 풀이 부족하면 fallbackScopes(같은 class 전체 scope)에서 보충
  return (
    <RandomProblems
      scopes={scopes}
      fallbackScopes={allClassScopes}
      allowExcludeBasic
      onRetry={resampleScopes}
    />
  );
};

export default QuizProblems;
